import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import FileDownload from 'js-file-download'

import {
    Badge,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    useTheme,
} from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import SearchIcon from '@material-ui/icons/Search'
import InventoryIcon from '@material-ui/icons/Kitchen'
import WarningIcon from '@material-ui/icons/WarningRounded'
import InboxIcon from '@material-ui/icons/InboxRounded'

import { Container } from '../../../components'
import {
    useAptConfig,
    useRootInfrastructure,
    useUnitInventory,
} from '../../../hooks'
import {
    buildAreaLabelContextFromUnit,
    exportUnitInventory,
    findUnitById,
    flattenUnitInventory,
    getUnitInventory,
    InventoryAsset,
    InventoryAssetPatchPayload,
    InventoryFilters,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'
import { AssetEditModal } from './AssetEditModal'
import {
    getFilteredGroupedInventory,
    InventoryFilterBar,
} from './InventoryFilterBar'
import { InventoryAssetCard } from './InventoryAssetCard'
import { InventoryUnitSideBar } from './InventoryUnitSideBar'

const defaultFilters: InventoryFilters = {
    search: '',
    area: null,
    condition: null,
    status: null,
}

export const UnitInventoryHome = () => {
    const theme = useTheme()
    const { tree, unitList } = useRootInfrastructure(true)
    const { getAreaConfigMap } = useAptConfig({ areaConfigList: true })
    const areaConfigMap = getAreaConfigMap()

    const {
        inventory,
        loading,
        error,
        loadInventory,
        saveAsset,
        deleteAsset,
        clearInventory,
    } = useUnitInventory()

    const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null)
    const [unitSearchQuery, setUnitSearchQuery] = useState('')
    const [viewTab, setViewTab] = useState(0)
    const [filters, setFilters] = useState<InventoryFilters>(defaultFilters)
    const [editAsset, setEditAsset] = useState<InventoryAsset | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<InventoryAsset | null>(
        null,
    )
    const [deleting, setDeleting] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [pendingCount, setPendingCount] = useState(0)

    const selectedUnit = useMemo(() => {
        if (selectedUnitId === null) {
            return undefined
        }
        return findUnitById(tree.root, selectedUnitId)
    }, [tree.version, selectedUnitId])

    const selectedUnitName = useMemo(() => {
        if (selectedUnitId === null) {
            return ''
        }
        return (
            unitList?.find((unit) => unit.id === selectedUnitId)?.name ?? 'unit'
        )
    }, [unitList, selectedUnitId])

    const areaLabelContext = useMemo(() => {
        if (!selectedUnit) {
            return undefined
        }
        const areaConfigNames = Object.fromEntries(
            Object.entries(areaConfigMap).map(([id, config]) => [
                Number(id),
                config.name,
            ]),
        )
        return buildAreaLabelContextFromUnit(
            selectedUnit.areas,
            areaConfigNames,
        )
    }, [selectedUnit, areaConfigMap])

    const refreshPendingCount = async (unitId: number) => {
        try {
            const response = await getUnitInventory(unitId, {
                confirmed: false,
            })
            setPendingCount(flattenUnitInventory(response.data).length)
        } catch {
            setPendingCount(0)
        }
    }

    useEffect(() => {
        if (selectedUnitId === null) {
            clearInventory()
            setPendingCount(0)
            return
        }

        setFilters(defaultFilters)
        const confirmed = viewTab === 0
        loadInventory(selectedUnitId, confirmed).catch(() => {
            toast.error('Unable to load inventory')
        })
        refreshPendingCount(selectedUnitId)
    }, [selectedUnitId, viewTab, clearInventory, loadInventory])

    const groupedInventory = useMemo(() => {
        if (!inventory) {
            return []
        }
        return getFilteredGroupedInventory(inventory, filters, areaLabelContext)
    }, [inventory, filters, areaLabelContext])

    const handleExport = async () => {
        if (selectedUnitId === null) {
            return
        }

        setExporting(true)
        try {
            const response = await exportUnitInventory(selectedUnitId)
            FileDownload(
                response.data,
                `${selectedUnitName.replace(/\s+/g, '_')}_inventory.csv`,
            )
        } catch (err) {
            console.error(err)
            toast.error('Could not export inventory')
        } finally {
            setExporting(false)
        }
    }

    const handleSaveAsset = async (
        assetId: number,
        payload: InventoryAssetPatchPayload,
        pendingPhotoFiles: File[],
    ) => {
        const updatedAsset = await saveAsset(assetId, payload, {
            useConfirmEndpoint: viewTab === 1,
            pendingPhotoFiles,
        })

        // Reload inventory in the background — don't let reload failure
        // mask the successful save from the caller (AssetEditModal).
        if (selectedUnitId !== null) {
            loadInventory(selectedUnitId, viewTab === 0).catch(() => {})
            refreshPendingCount(selectedUnitId).catch(() => {})
        }

        return updatedAsset
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) {
            return
        }

        setDeleting(true)
        try {
            await deleteAsset(deleteTarget.id)
            toast.success('Asset deleted')
            setDeleteTarget(null)
            if (selectedUnitId !== null) {
                await refreshPendingCount(selectedUnitId)
            }
        } catch (err) {
            console.error(err)
            toast.error('Could not delete asset')
        } finally {
            setDeleting(false)
        }
    }

    const renderContent = () => {
        if (selectedUnitId === null) {
            return (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                    }}
                >
                    <InventoryIcon
                        style={{
                            fontSize: 64,
                            color: theme.palette.grey[300],
                        }}
                    />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Select a unit to view inventory
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.disabled,
                            maxWidth: 320,
                            textAlign: 'center',
                        }}
                    >
                        Choose a unit from the sidebar to browse its confirmed
                        assets, edit details, or review pending items.
                    </span>
                </Container>
            )
        }

        if (loading) {
            return (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(1.5),
                    }}
                >
                    <CircularProgress size={36} />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Loading inventory...
                    </span>
                </Container>
            )
        }

        if (error) {
            return (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(1.5),
                    }}
                >
                    <WarningIcon
                        style={{
                            fontSize: 48,
                            color: theme.palette.error.light,
                        }}
                    />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body1),
                            color: theme.palette.error.main,
                            fontWeight: 500,
                        }}
                    >
                        {error}
                    </span>
                </Container>
            )
        }

        if (!inventory || flattenUnitInventory(inventory).length === 0) {
            return (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                    }}
                >
                    <InboxIcon
                        style={{
                            fontSize: 56,
                            color: theme.palette.grey[300],
                        }}
                    />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                        }}
                    >
                        {viewTab === 0
                            ? 'No saved inventory for this unit'
                            : 'No assets pending review'}
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.disabled,
                        }}
                    >
                        {viewTab === 0
                            ? 'Run a walkthrough on mobile to discover assets automatically.'
                            : 'All items have been reviewed.'}
                    </span>
                </Container>
            )
        }

        return (
            <>
                <InventoryFilterBar
                    inventory={inventory}
                    filters={filters}
                    areaLabelContext={areaLabelContext}
                    onFiltersChange={setFilters}
                />
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        overflow: 'auto',
                        padding: theme.spacing(2.5),
                        gap: theme.spacing(3),
                    }}
                >
                    {groupedInventory.length === 0 && (
                        <Container
                            style={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: theme.spacing(1),
                                paddingTop: theme.spacing(4),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                No assets match the current filters
                            </span>
                        </Container>
                    )}
                    {groupedInventory.map((group) => (
                        <Container
                            key={group.displayLabel}
                            style={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <Container
                                style={{
                                    alignItems: 'center',
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.overline,
                                        ),
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {group.displayLabel.toUpperCase()}
                                </span>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 500,
                                        color: theme.palette.text.disabled,
                                        backgroundColor:
                                            theme.palette.grey[100],
                                        borderRadius: 10,
                                        padding: '2px 8px',
                                    }}
                                >
                                    {group.assets.length}
                                </span>
                                <span
                                    style={{
                                        flex: 1,
                                        height: 1,
                                        backgroundColor: theme.palette.divider,
                                    }}
                                />
                            </Container>
                            <Container
                                style={{
                                    flexWrap: 'wrap',
                                    gap: theme.spacing(1.5),
                                    alignItems: 'stretch',
                                }}
                            >
                                {group.assets.map((asset) => (
                                    <InventoryAssetCard
                                        key={asset.id}
                                        asset={asset}
                                        onEdit={setEditAsset}
                                        onDelete={setDeleteTarget}
                                    />
                                ))}
                            </Container>
                        </Container>
                    ))}
                </Container>
            </>
        )
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                overflow: 'hidden',
                height: '100%',
                backgroundColor: theme.palette.grey[50],
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1.5, 2.5),
                    gap: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
            >
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search units..."
                    value={unitSearchQuery}
                    onChange={(event) => setUnitSearchQuery(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    fontSize="small"
                                    style={{
                                        color: theme.palette.text.disabled,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    style={{ minWidth: 220, maxWidth: 300 }}
                />

                {selectedUnitId !== null && (
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: theme.spacing(1),
                            padding: theme.spacing(0.5, 1.5),
                            borderRadius: 8,
                            backgroundColor: `${theme.palette.primary.main}08`,
                            border: `1px solid ${theme.palette.primary.main}20`,
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                            }}
                        >
                            {selectedUnitName}
                        </span>
                    </Container>
                )}

                <Tabs
                    value={viewTab}
                    onChange={(_, value) => setViewTab(value)}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        label="Saved inventory"
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        label={
                            <Badge
                                badgeContent={pendingCount}
                                color="secondary"
                                max={99}
                            >
                                <span
                                    style={{
                                        paddingRight: pendingCount ? 12 : 0,
                                    }}
                                >
                                    Pending review
                                </span>
                            </Badge>
                        }
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    />
                </Tabs>
                <Container
                    style={{ marginLeft: 'auto', gap: theme.spacing(1) }}
                >
                    {/* <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            exporting ? (
                                <CircularProgress size={14} />
                            ) : (
                                <GetAppIcon fontSize="small" />
                            )
                        }
                        disabled={
                            selectedUnitId === null ||
                            exporting ||
                            viewTab === 1
                        }
                        onClick={handleExport}
                        style={{ textTransform: 'none', fontWeight: 500 }}
                    >
                        Export CSV
                    </Button> */}
                </Container>
            </Container>

            <Container
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <InventoryUnitSideBar
                    selectedUnitId={selectedUnitId}
                    setSelectedUnitId={setSelectedUnitId}
                    searchQuery={unitSearchQuery}
                />

                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {renderContent()}
                </Container>
            </Container>

            <AssetEditModal
                open={Boolean(editAsset)}
                asset={editAsset}
                isPendingReview={viewTab === 1}
                onClose={() => setEditAsset(null)}
                onSave={handleSaveAsset}
            />

            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                PaperProps={{ style: { borderRadius: 12 } }}
            >
                <DialogTitle>
                    <Container style={{ alignItems: 'center', gap: 12 }}>
                        <WarningIcon
                            style={{ color: theme.palette.error.main }}
                        />
                        <span>Delete asset</span>
                    </Container>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove{' '}
                        <strong>{deleteTarget?.name}</strong> from inventory?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: theme.spacing(1, 3, 2) }}>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={deleting}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={deleting}
                        onClick={handleConfirmDelete}
                        style={{ textTransform: 'none' }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
